import { type ComponentProps, type FC, memo, useMemo } from "react";
import dayjs from "dayjs";
import { cx } from "@stubhub/broadway-foundation/css";
import { Text } from "@stubhub/broadway-web";
import { BaseballIcon, CarIcon, NotificationIcon } from "@stubhub/broadway-web/icons/functional";
import { EventGridListItemTags, useFormattedEventDateTime } from "@stubhub/ensemble-discovery";
import type { EventListGridItem } from "src/http/types";
import { Content, FormatContent, useFormattedContent } from "src/shared/components/content";
import { BaseDateCardPortion } from "src/website-apps/responsive/stubhub-performer/components/calendar/BaseDateCardPortion";
import { useDateCardEventState } from "src/website-apps/responsive/stubhub-performer/components/calendar/hooks/useDateCardEventState";
import { useTeamThemes } from "src/website-apps/shared/hooks/useTeamThemes";
import { calendarTagRecipe, calendarTagsRecipe, mlbDateCardPortion } from "./styles";

interface Props {
  /** Event object */
  event: EventListGridItem;
  /** Event is home game for primary team */
  isHomeGame: boolean;
  /** Handler to open notification modal */
  openNotificationHandler: (eventId: number) => void;
  /** Click event handler */
  onClick: (event: EventListGridItem) => void;
  /** Mouse enter event handler */
  onMouseEnter: (event: EventListGridItem) => void;
  /** Position index of the parent day card in the calendar (only days with events are counted) */
  calendarIndex: number;
}

/**
 * Condensed portion component for displaying a single MLB event within a multi-event date card.
 * Used for double-headers and other scenarios where multiple games occur on the same day.
 * Shows opponent team logo, game time, and notification icon for sold-out games.
 *
 * Per-event state (availability, merch tags, analytics, click handling, primary-team
 * theming) is owned by `useDateCardEventState`; this component handles MLB-specific
 * presentation only.
 */
export const MlbDateCardPortion: FC<Props> = memo(
  ({ event, isHomeGame, openNotificationHandler, onClick, onMouseEnter, calendarIndex }) => {
    const isParkingEvent = !!event.isParkingEvent;

    const {
      available,
      eventState,
      merchTags,
      inViewTrackers,
      primaryColor,
      handleClick,
      handleMouseEnter,
      handleInView,
    } = useDateCardEventState({
      event,
      isHomeGame,
      calendarIndex,
      openNotificationHandler,
      onClick,
      onMouseEnter,
    });
    const noTicketsAvailable = !available;

    const { formattedTime } = useFormattedEventDateTime(
      event.eventMetadata?.common?.eventStartDateTime,
      event.eventMetadata?.common?.eventEndDateTime ?? undefined,
      event.eventMetadata?.common?.venueTimeZoneOffset,
      event.onSaleDateTimeData?.onSaleDateTime
        ? dayjs(event.onSaleDateTimeData.onSaleDateTime).unix() * 1000
        : undefined,
      event.eventMetadata?.common?.passLength ?? undefined,
    );

    const teamThemes = useTeamThemes(event.eventMetadata);

    const secondaryTeamMetadataKey = isHomeGame
      ? event.eventMetadata?.sports?.awayTeamId
      : event.eventMetadata?.sports?.homeTeamId;
    const secondaryTeamMetadata = secondaryTeamMetadataKey
      ? event.eventMetadata?.metadataForAllCategoriesInEvent?.[secondaryTeamMetadataKey]
      : null;

    /** "\{0\} Logo" */
    const logoAltText = useFormattedContent("cid$#3561796#", [secondaryTeamMetadata?.shortName ?? "team"]);

    const secondaryTeamTheme = isHomeGame ? teamThemes.awayTeamData.theme : teamThemes.homeTeamData.theme;

    // Slot recipes type slot classes as `string | undefined`; every slot here
    // has a base style so the cast to a dense record removes `?? ""` boilerplate.
    const portionStyles = mlbDateCardPortion({ ticketAvailability: noTicketsAvailable ? "soldOut" : "available" });
    const notificationIconClasses: ComponentProps<typeof NotificationIcon>["classes"] = useMemo(
      () => ({ root: cx(portionStyles.notificationIcon) }),
      [portionStyles.notificationIcon],
    );

    const tagsClasses = calendarTagsRecipe({ spacing: "padded" });
    const tagClasses = calendarTagRecipe();
    // From-price is gated by TPerformerFromPriceInEventList / MPerformerFromPriceInEventList
    // upstream — only populated when the user is in variant.
    const fromPrice = event.contextSensitiveFormattedMinPrice ?? null;

    const body = useMemo(
      () => (
        <>
          <div className={portionStyles.body}>
            <div>
              {secondaryTeamTheme.transparentPartnerLogoUrl ? (
                <img
                  className={portionStyles.logo}
                  src={secondaryTeamTheme.transparentPartnerLogoUrl}
                  alt={logoAltText}
                  role="presentation"
                />
              ) : (
                <BaseballIcon size={32} />
              )}
            </div>
            <div className={portionStyles.text}>
              <Text className={portionStyles.textLine} variant="label" size="small">
                {isHomeGame ? /* "vs" */ <Content id="cid$#3561795#" /> : /* "@" */ <Content id="cid$#3561783#" />}
                &nbsp;
                {secondaryTeamMetadata?.shortName ?? /* "TBD" */ <Content id="cid$#3983126#" />}
              </Text>
              <Text className={portionStyles.subtleLine} variant="label" size="extra-small">
                {formattedTime}
              </Text>
              {noTicketsAvailable ? (
                <Text className={portionStyles.textLine} variant="label" size="extra-small">
                  {/* "Sold out" */}
                  <Content id="cid$#3979713#" />
                </Text>
              ) : fromPrice ? (
                <Text className={portionStyles.subtleLine} variant="label" size="extra-small">
                  {/* "From {0}" */}
                  <FormatContent id="cid$#4099723#" params={[fromPrice]} />
                </Text>
              ) : null}
            </div>
            {isParkingEvent ? (
              <CarIcon classes={notificationIconClasses} size={16} />
            ) : (
              noTicketsAvailable && <NotificationIcon classes={notificationIconClasses} size={16} />
            )}
          </div>
          {merchTags.length > 0 && (
            <EventGridListItemTags legacy tags={merchTags} classes={tagsClasses} tagClasses={tagClasses} />
          )}
        </>
      ),
      [
        formattedTime,
        fromPrice,
        isHomeGame,
        isParkingEvent,
        logoAltText,
        noTicketsAvailable,
        secondaryTeamMetadata?.shortName,
        secondaryTeamTheme,
        merchTags,
        tagsClasses,
        tagClasses,
        portionStyles.body,
        portionStyles.text,
        portionStyles.textLine,
        portionStyles.subtleLine,
        portionStyles.logo,
        notificationIconClasses,
      ],
    );

    return (
      <BaseDateCardPortion
        eventState={eventState}
        event={event}
        eventURL={event.url ?? "#"}
        primaryColor={primaryColor}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onInView={handleInView}
      >
        {body}
        {inViewTrackers}
      </BaseDateCardPortion>
    );
  },
);
MlbDateCardPortion.displayName = "MlbDateCardPortion";
